/* ──────────────────────────────────────────────────────────────
   src/pages/reports/profesores/AsignaturasProfesorPage.tsx
   Informe: asignaturas que imparte cada profesor + alumnos
   ────────────────────────────────────────────────────────────── */
import { useState } from "react";
import dayjs from "dayjs";
import {
  Stack,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Collapse,
  IconButton,
  LinearProgress,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
import { useTranslation } from "react-i18next";
import type { AsigProfesorRow, AsigAlumnoRow } from "../../../api/reports";
import { useAsignaturasProfesorReport } from "../../../hooks/useReportJob";
import { getAlumnosAsignatura } from "../../../api/alumnos";

export default function AsignaturasProfesorPage() {
  const { t } = useTranslation<"translation">();
  /* ───── filtros ───── */
  const [desde, setDesde] = useState(dayjs().format("YYYY-MM-DD"));
  const [search, setSearch] = useState("");

  /* ───── worker (polling) ───── */
  const [jobId, setJobId] = useState<string>();
  const { run, useStatus } = useAsignaturasProfesorReport();
  const { data: status } = useStatus(jobId);

  /* ───── estado UI ───── */
  const [openProf, setOpenProf] = useState<number>(); // profesor expandido
  const [openAsig, setOpenAsig] = useState<string>(); // "profId-asigId"
  const [detCache, setDetCache] = useState<Record<string, AsigAlumnoRow[]>>({});
  const [loadingDet, setLoading] = useState<string>(); // rowId que carga

  /* ───── lanzar informe ───── */
  const generate = async () => {
    const { jobId } = await run({ desde });
    setJobId(jobId);
    /* reseteamos UI */
    setOpenProf(undefined);
    setOpenAsig(undefined);
    setDetCache({});
  };

  /* ═════════════ agrupación ═════════════ */
  const filas: AsigProfesorRow[] = status?.result?.rows ?? [];

  const grouped = filas.reduce<
    Record<
      number,
      {
        profesor: string;
        asignaturas: AsigProfesorRow[];
      }
    >
  >((acc, r) => {
    (acc[r.profesorId] = acc[r.profesorId] ?? {
      profesor: r.profesor,
      asignaturas: [],
    }).asignaturas.push(r);
    return acc;
  }, {});

  const profesores = Object.values(grouped)
    .filter((g) => g.profesor.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => a.profesor.localeCompare(b.profesor));

  /* ═════════════ UI ═════════════ */
  return (
    <Stack spacing={3}>
      <Typography variant="h5">{t("asignaturasProfesor.titulo")}</Typography>

      {/* filtros */}
      <Stack direction="row" spacing={2} alignItems="center">
        <TextField
          type="date"
          label={t("asignaturasProfesor.filtros.desde")}
          value={desde}
          onChange={(e) => setDesde(e.target.value)}
        />
        <TextField
          size="small"
          placeholder={t<string>("asignaturasProfesor.filtros.buscar")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 260 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Button variant="contained" onClick={generate}>
          {t("asignaturasProfesor.filtros.generar")}
        </Button>
      </Stack>

      {/* progreso */}
      {status && !status.done && <LinearProgress variant="determinate" value={status.progress} />}

      {/* tabla */}
      {status?.done && (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>
                  <b>{t("asignaturasProfesor.tabla.profesor")}</b>
                </TableCell>
                <TableCell align="right">
                  <b>{t("asignaturasProfesor.tabla.numAsignaturas")}</b>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {profesores.map((p) => (
                <>
                  {/* profesor */}
                  <TableRow key={p.profesor}>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => {
                          const id = p.asignaturas[0].profesorId;
                          setOpenProf(openProf === id ? undefined : id);
                          setOpenAsig(undefined);
                        }}
                      >
                        {openProf === p.asignaturas[0].profesorId ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </TableCell>
                    <TableCell>{p.profesor}</TableCell>
                    <TableCell align="right">{p.asignaturas.length}</TableCell>
                  </TableRow>

                  {/* asignaturas */}
                  <TableRow>
                    <TableCell style={{ padding: 0 }} colSpan={3}>
                      <Collapse in={openProf === p.asignaturas[0].profesorId} unmountOnExit>
                        <Paper variant="outlined" sx={{ m: 1 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>
                                  {t("asignaturasProfesor.asignaturas.asignatura")}
                                </TableCell>
                                <TableCell align="right">
                                  {t("asignaturasProfesor.asignaturas.numAlumnos")}
                                </TableCell>
                                <TableCell align="right">
                                  {t("asignaturasProfesor.asignaturas.numReservas")}
                                </TableCell>
                                <TableCell align="right">
                                  {t("asignaturasProfesor.asignaturas.ultimaClase")}
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {p.asignaturas.map((a) => {
                                const rowId = `${p.asignaturas[0].profesorId}-${a.asignaturaId}`;
                                const alumnos = detCache[rowId] ?? [];
                                return (
                                  <>
                                    {/* asignatura resumen */}
                                    <TableRow key={a.asignaturaId}>
                                      <TableCell>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                          <span>{a.asignatura}</span>
                                          <IconButton
                                            size="small"
                                            onClick={async () => {
                                              if (openAsig === rowId) {
                                                setOpenAsig(undefined);
                                                return;
                                              }
                                              if (!detCache[rowId]) {
                                                setLoading(rowId);
                                                const data = await getAlumnosAsignatura(
                                                  p.asignaturas[0].profesorId,
                                                  a.asignaturaId,
                                                  desde
                                                );
                                                setDetCache((s) => ({
                                                  ...s,
                                                  [rowId]: data,
                                                }));
                                                setLoading("");
                                              }
                                              setOpenAsig(rowId);
                                            }}
                                          >
                                            {openAsig === rowId ? (
                                              <ExpandLess fontSize="small" />
                                            ) : (
                                              <ExpandMore fontSize="small" />
                                            )}
                                          </IconButton>
                                        </Stack>
                                      </TableCell>
                                      <TableCell align="right">{a.numAlumnos}</TableCell>
                                      <TableCell align="right">{a.numReservas}</TableCell>
                                      <TableCell align="right">{a.ultimaClase}</TableCell>
                                    </TableRow>

                                    {/* alumnos detalle */}
                                    <TableRow>
                                      <TableCell style={{ padding: 0 }} colSpan={3}>
                                        <Collapse in={openAsig === rowId} unmountOnExit>
                                          <Paper variant="outlined" sx={{ m: 1 }}>
                                            {loadingDet === rowId && (
                                              <Stack alignItems="center" py={2}>
                                                <CircularProgress size={20} />
                                              </Stack>
                                            )}
                                            {!loadingDet && (
                                              <Table size="small">
                                                <TableHead>
                                                  <TableRow>
                                                    <TableCell>
                                                      {t("asignaturasProfesor.detalle.alumno")}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                      {t("asignaturasProfesor.detalle.ultimaClase")}
                                                    </TableCell>
                                                  </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                  {alumnos.map((al) => (
                                                    <TableRow key={al.alumnoId}>
                                                      <TableCell>{al.alumno}</TableCell>
                                                      <TableCell align="right">
                                                        {al.ultimaClase}
                                                      </TableCell>
                                                    </TableRow>
                                                  ))}
                                                </TableBody>
                                              </Table>
                                            )}
                                          </Paper>
                                        </Collapse>
                                      </TableCell>
                                    </TableRow>
                                  </>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </Paper>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Stack>
  );
}
